import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type LegalDoc = 'privacy' | 'terms' | null;
const legalContent = {
  privacy: {
    title: 'Política de Privacidad',
    updated: 'Última actualización: Mayo 2026',
    sections: [{
      heading: '1. Información que Recopilamos',
      body: 'Recopilamos información que usted nos proporciona directamente al registrarse, realizar una compra o comunicarse con nosotros. Esto puede incluir su nombre, dirección de correo electrónico, información de pago y cualquier otra información que decida compartir.'
    }, {
      heading: '2. Uso de la Información',
      body: 'Utilizamos su información personal para procesar transacciones, entregar el contenido del programa, enviar comunicaciones relacionadas con su cuenta, mejorar nuestros servicios y cumplir con obligaciones legales.'
    }, {
      heading: '3. Compartir Información',
      body: 'No vendemos ni alquilamos su información personal a terceros. Podemos compartir información con proveedores de servicios de confianza que nos ayudan a operar el sitio, siempre bajo estrictos acuerdos de confidencialidad.'
    }, {
      heading: '4. Seguridad de los Datos',
      body: 'Implementamos medidas técnicas y organizativas razonables para proteger su información personal contra el acceso no autorizado, la alteración, divulgación o destrucción.'
    }, {
      heading: '5. Cookies y Tecnologías Similares',
      body: 'Utilizamos cookies y tecnologías similares para mejorar su experiencia, analizar el uso del sitio y personalizar el contenido. Puede controlar las cookies a través de la configuración de su navegador.'
    }, {
      heading: '6. Sus Derechos',
      body: 'Usted tiene derecho a acceder, corregir o eliminar su información personal. También puede oponerse al procesamiento de sus datos o solicitar su portabilidad en cualquier momento.'
    }, {
      heading: '7. Cambios a esta Política',
      body: 'Podemos actualizar esta política periódicamente. Le notificaremos sobre cambios significativos publicando la nueva política en esta página con una fecha de actualización revisada.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    updated: 'Última actualización: Mayo 2026',
    sections: [{
      heading: '1. Aceptación de los Términos',
      body: 'Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, usted acepta estar sujeto a estos Términos y Condiciones. Si no está de acuerdo, por favor no utilice nuestros servicios.'
    }, {
      heading: '2. Descripción del Servicio',
      body: '"Primeras Ventas en 48 Horas" es un programa digital educativo que proporciona estrategias, métodos y recursos para ayudar a emprendedores a generar sus primeras ventas. El programa se entrega en formato digital de acceso inmediato.'
    }, {
      heading: '3. Licencia de Uso',
      body: 'Al adquirir el programa, recibe una licencia personal, no transferible y no exclusiva para acceder al contenido. No está permitido revender, redistribuir, compartir o reproducir el material sin autorización expresa por escrito.'
    }, {
      heading: '4. Pagos y Acceso',
      body: 'Los pagos se procesan de forma segura a través de plataformas de terceros. Una vez completado el pago, recibirá acceso inmediato al contenido del programa. Todos los precios están en dólares estadounidenses (USD).'
    }, {
      heading: '5. Política de Reembolsos',
      body: 'Ofrecemos una garantía de satisfacción. Si no está conforme con el programa, puede solicitar un reembolso dentro del período especificado en su confirmación de compra, siguiendo el procedimiento indicado.'
    }, {
      heading: '6. Descargo de Responsabilidad',
      body: 'Los resultados varían según el esfuerzo, dedicación y circunstancias individuales. No garantizamos ingresos específicos. El contenido es educativo y no constituye asesoramiento financiero, legal o profesional.'
    }, {
      heading: '7. Propiedad Intelectual',
      body: 'Todo el contenido del programa, incluyendo textos, videos, imágenes y materiales, es propiedad exclusiva nuestra y está protegido por las leyes de derechos de autor y propiedad intelectual.'
    }, {
      heading: '8. Limitación de Responsabilidad',
      body: 'En ningún caso seremos responsables por daños indirectos, incidentales, especiales o consecuentes derivados del uso o la imposibilidad de uso de nuestros productos o servicios.'
    }, {
      heading: '9. Modificaciones',
      body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor al publicarse en esta página. El uso continuado del servicio constituye aceptación de los nuevos términos.'
    }]
  }
};
function LegalModal({
  doc,
  onClose
}: {
  doc: LegalDoc;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!doc) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-37">
      {doc && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-38">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-39">
            <div className="flex items-start justify-between gap-4 px-6 md:px-8 py-5 border-b border-gray-200 bg-gradient-to-br from-[#1e3a8a] to-[#1e2a5a]" data-id="element-40">
              <div data-id="element-41">
                <h2 id="legal-modal-title" className="text-2xl md:text-3xl font-bold text-white" data-id="element-42">
                  {legalContent[doc].title}
                </h2>
                <p className="text-sm text-white/70 mt-1" data-id="element-43">
                  {legalContent[doc].updated}
                </p>
              </div>
              <button onClick={onClose} aria-label="Cerrar" className="shrink-0 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors" data-id="element-44">
                <XIcon className="w-6 h-6" data-id="element-45" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 md:px-8 py-6 space-y-6" data-id="element-46">
              {legalContent[doc].sections.map((section, idx) => <div key={idx} className="space-y-2" data-id="element-47">
                  <h3 className="text-lg md:text-xl font-bold text-[#1e3a8a]" data-id="element-48">
                    {section.heading}
                  </h3>
                  <p className="text-base text-gray-700 leading-relaxed" data-id="element-49">
                    {section.body}
                  </p>
                </div>)}
            </div>

            <div className="px-6 md:px-8 py-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-50">
              <button onClick={onClose} className="bg-[#f97316] hover:bg-[#ea580c] text-white font-semibold px-6 py-2.5 rounded-lg transition-colors" data-id="element-51">
                Entendido
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [openDoc, setOpenDoc] = useState<LegalDoc>(null);
  return <>
      <footer className="w-full py-8 bg-[#1e2a5a] text-white" data-id="element-52">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl" data-id="element-53">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-54">
            <p className="text-sm text-gray-300" data-id="element-55">
              © 2026 Primeras Ventas en 48 Horas. Todos los derechos
              reservados.
            </p>
            <div className="flex gap-6 text-sm" data-id="element-56">
              <button type="button" onClick={() => setOpenDoc('terms')} className="text-gray-300 hover:text-[#f97316] transition-colors" data-id="element-57">
                Términos y Condiciones
              </button>
              <button type="button" onClick={() => setOpenDoc('privacy')} className="text-gray-300 hover:text-[#f97316] transition-colors" data-id="element-58">
                Política de Privacidad
              </button>
            </div>
          </div>
        </div>
      </footer>

      <LegalModal doc={openDoc} onClose={() => setOpenDoc(null)} data-id="element-59" />
    </>;
}